import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Stepper, Step, StepLabel,
  TextField, FormControl, FormLabel, Radio, RadioGroup,
  FormControlLabel, Alert, CircularProgress, Divider,
  Checkbox, InputAdornment, Paper, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AccountBalance, Security, CheckCircle, Gavel, Shield, Lock, VerifiedUser
} from '@mui/icons-material';
import escrowService from '../../services/escrowService';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

import DialogCloseButton from '../common/DialogCloseButton';
import { brand, text, bg, border, shadow, status as statusColors } from '../../theme/colors';

const STAGE_CHECK_FEE_USD = 50;
// A client whose phone or country of residence is outside Zimbabwe cannot
// see the site themselves: the independent stage check is on by default.
const isDiasporaUser = (u) => {
  const cc = String(u?.countryCode || u?.diasporaLocation || '').toUpperCase();
  if (cc && cc !== 'ZW') return true;
  const digits = String(u?.phoneNumber || u?.phone || '').replace(/\D/g, '');
  return Boolean(digits) && digits.length >= 10 && !digits.startsWith('263') && !digits.startsWith('07');
};

const WALLET_TERMS = [
  {
    title: '1. WALLET PURPOSE',
    text: 'Funds deposited into your Eqaya Wallet are held securely and used to pay contractors, verifiers, and other project-related service providers upon completion and approval of project milestones. Wallet funds may also be used to purchase materials directly through the platform.'
  },
  {
    title: '2. FUND PROTECTION',
    text: 'All wallet funds are held in a segregated trust account. Eqaya does not commingle your funds with operating capital. Your money is protected and only disbursed upon your explicit authorization or through the automatic release process described below.'
  },
  {
    title: '3. MILESTONE-BASED RELEASES',
    text: 'Payments are released when you approve a completed milestone. If a contractor submits a milestone as complete and you do not confirm or dispute within 72 hours, the funds for that milestone will be automatically released. You retain control over disbursement by actively reviewing milestones within this window.'
  },
  {
    title: '4. PLATFORM FEE',
    text: 'A 2.5% platform fee is applied to each milestone payment at the time of disbursement. This fee covers payment processing, dispute resolution, and platform maintenance.'
  },
  {
    title: '5. REFUND POLICY',
    text: 'Unused wallet funds may be withdrawn at any time via ACH or Wire transfer. Funds allocated to active milestones cannot be withdrawn until the milestone is completed or cancelled. Withdrawal processing takes 2–5 business days (ACH) or 1 business day (Wire).'
  },
  {
    title: '6. DISPUTE RESOLUTION',
    text: 'In the event of a dispute, Eqaya will hold the disputed funds until the dispute is resolved through our mediation process. Both parties will have the opportunity to present evidence. Disputes must be raised within 72 hours of milestone submission to prevent automatic release.'
  },
  {
    title: '7. ACCOUNT SECURITY',
    text: 'You are responsible for maintaining the security of your account credentials. Eqaya uses bank-level encryption to protect all financial transactions and personal data.'
  },
  {
    title: '8. LIABILITY',
    text: 'Eqaya acts as a neutral intermediary and is not liable for the quality of work performed by contractors or verifiers. Our role is limited to secure fund custody and milestone-based payment processing.'
  },
  {
    title: '9. TERMINATION',
    text: 'You may close your wallet at any time. Any remaining balance will be refunded to your original payment method within 5–10 business days, minus any applicable fees for completed transactions.'
  }
];

/**
 * `suggestion` (from the project hub's `deposit`): { amount, stageName,
 * stageIndex, stageCount, total, remaining, available, currency }. When the
 * project has priced stages the dialog asks for the next stage only — the
 * whole-quotation figure is what makes clients go quiet — with "whole
 * project" and "another amount" one tap away.
 */
const EscrowSetupDialog = ({ open, onClose, project, contractor, onSuccess, skipTerms = false, suggestion = null }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Full-screen on phones so the action button is always on screen: Cathy
  // entered the amount and never found where to pay (2026-09-14) — the
  // "Fund Wallet via Stripe" button sat below the fold of a 70vh dialog.
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Step 1: Terms
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Step 2: Fund
  const [totalAmount, setTotalAmount] = useState('');
  const [fundingMethod, setFundingMethod] = useState('debit_card');
  // 'stage' | 'whole' | 'custom' — which figure the client chose.
  const [amountChoice, setAmountChoice] = useState('stage');
  // Independent stage check: a verified person visits when the stage is
  // done and sends a photo-backed report. Added to the same payment.
  const { user } = useAuth();
  const [stageCheck, setStageCheck] = useState(false);
  const staged = Boolean(suggestion && suggestion.stageCount > 0 && suggestion.amount > 0);
  const multiStage = staged && suggestion.stageCount > 1;

  const steps = ['Accept Terms', 'Fund Wallet'];

  useEffect(() => {
    if (open) {
      // Prefill the accepted-quotation amount (project budget) so the client
      // isn't staring at $0.00 wondering what to enter — Cathy read exactly
      // that as "no deposit needed" and the deal stalled (2026-09-11).
      // Still editable for partial funding.
      if (skipTerms) {
        setActiveStep(1);
        setTermsAccepted(true);
      } else {
        setActiveStep(0);
        setTermsAccepted(false);
      }
      // Next stage first when the project has priced stages; otherwise the
      // accepted-quotation total. Always editable.
      if (suggestion && suggestion.stageCount > 0 && suggestion.amount > 0) {
        setTotalAmount(String(suggestion.amount));
        setAmountChoice('stage');
      } else {
        setTotalAmount(project?.budget && parseFloat(project.budget) > 0 ? String(project.budget) : (suggestion?.amount ? String(suggestion.amount) : ''));
        setAmountChoice('custom');
      }
      setFundingMethod('debit_card');
      setStageCheck(isDiasporaUser(user));
      setError('');
    }
  }, [open, project, skipTerms, suggestion, user]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: project?.currency || 'USD'
    }).format(amount);
  };

  const getStripePaymentMethods = (method) => {
    if (['debit_card', 'credit_card'].includes(method)) return ['card'];
    if (['ach', 'wire'].includes(method)) return ['us_bank_account'];
    return ['card'];
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleFundWallet = async () => {
    try {
      setLoading(true);
      setError('');

      const amount = parseFloat(totalAmount);
      if (!amount || amount <= 0) {
        setError('Please enter a valid amount');
        setLoading(false);
        return;
      }

      // 1. Create escrow/wallet account if it doesn't exist yet
      let escrowId = project?.escrow?.id;
      if (!escrowId) {
        const payload = {
          projectId: project.id,
          totalAmount: amount,
          currency: project?.currency || 'USD',
          fundingSource: fundingMethod
        };
        if (contractor?.id) {
          payload.contractorUserId = contractor.id;
        }
        const escrow = await escrowService.createEscrowAccount(payload);
        escrowId = escrow.id;
      }

      if (!escrowId) {
        setError('Failed to create wallet. Please try again.');
        setLoading(false);
        return;
      }

      // 1b. Independent stage check, paid on the same checkout.
      let stageCheckRequestId;
      if (stageCheck) {
        try {
          const pc = await api.post('/property-checks', {
            packageSlug: 'basic_photo',
            projectId: project.id,
            milestoneId: suggestion?.stageId || undefined,
            paymentMethod: 'with_deposit',
            description: `Independent check of ${suggestion?.stageName ? `stage "${suggestion.stageName}"` : 'the work'} on "${project?.title || 'my project'}": confirm what has been built matches the stage, with dated GPS-tagged photos.`,
            scheduling: { flexibility: 'flexible' },
          });
          stageCheckRequestId = pc.data?.data?.propertyCheck?.id;
        } catch (pcErr) {
          setError(pcErr.response?.data?.error || 'Could not add the stage check. Untick it to fund without one.');
          setLoading(false);
          return;
        }
      }
      // 2. Create Stripe Checkout Session
      const response = await api.post('/stripe/create-checkout-session', {
        escrowId,
        amount,
        stageCheckRequestId,
        currency: project?.currency || 'USD',
        paymentMethodTypes: getStripePaymentMethods(fundingMethod),
        successUrl: `${window.location.origin}/projects/${project.id}?wallet_funded=true&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/projects/${project.id}?wallet_cancelled=true`
      });

      // 3. Redirect to Stripe Checkout
      const checkoutUrl = response.data?.data?.checkoutUrl || response.data?.checkoutUrl;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        throw new Error('Failed to get checkout URL');
      }
    } catch (err) {
      console.error('Error funding wallet:', err);
      setError(err.response?.data?.error || err.message || 'Failed to initiate payment');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            {/* Trust messaging banner */}
            <Box sx={{
              p: 2.5, mb: 3, borderRadius: 2,
              bgcolor: `${brand.primary}08`,
              border: `1px solid ${brand.primary}20`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Shield sx={{ color: brand.primary, fontSize: 28 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: brand.primaryDark }}>
                  Your Money Is Safe
                </Typography>
              </Box>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {[
                  { icon: <Lock sx={{ fontSize: 16, color: brand.primary }} />, text: 'Funds held in a segregated trust account — never mixed with operating funds' },
                  { icon: <VerifiedUser sx={{ fontSize: 16, color: brand.primary }} />, text: 'Released only when you approve completed work' },
                  { icon: <Security sx={{ fontSize: 16, color: brand.primary }} />, text: '72-hour review window before any automatic release' },
                ].map((item, i) => (
                  <Box key={i} component="li" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75, listStyle: 'none', ml: -2.5 }}>
                    {item.icon}
                    <Typography variant="body2" sx={{ color: text.secondary }}>{item.text}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Paper
              variant="outlined"
              sx={{
                maxHeight: 400,
                overflow: 'auto',
                p: 3,
                mb: 3,
                bgcolor: 'grey.50'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Gavel color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Eqaya Wallet Terms of Service
                </Typography>
              </Box>

              {WALLET_TERMS.map((term, index) => (
                <Box key={index} sx={{ mb: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {term.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {term.text}
                  </Typography>
                </Box>
              ))}
            </Paper>

            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  I have read and agree to the Eqaya Wallet Terms of Service
                </Typography>
              }
            />
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Fund Your Wallet
            </Typography>

            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              p: 2, mb: 3, borderRadius: 2,
              bgcolor: `${statusColors.success}08`,
              border: `1px solid ${statusColors.success}20`,
            }}>
              <Shield sx={{ color: statusColors.success, fontSize: 24 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: text.primary }}>
                  Funding wallet for "{project?.title}"
                </Typography>
                <Typography variant="body2" sx={{ color: text.secondary }}>
                  {multiStage ? (
                    <>
                      <strong>You only fund the stage that&apos;s about to start</strong> — not the
                      whole {formatCurrency(suggestion.total)}. It stays in your wallet, under your
                      control, and is released to the contractor only when you approve that stage.
                      Nothing is paid out today.
                      <strong> That&apos;s the Eqaya Guarantee.</strong>
                    </>
                  ) : (
                    <>
                      <strong>Deposit the amount now to start the work</strong> — it stays in
                      your wallet, under your control, and is only released to the
                      contractor as you approve completed work. Nothing is paid out today.
                      <strong> That&apos;s the Eqaya Guarantee.</strong>
                    </>
                  )}
                </Typography>
              </Box>
            </Box>

            {staged && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <FormLabel sx={{ mb: 1, fontWeight: 600 }}>How much to deposit</FormLabel>
                <RadioGroup
                  value={amountChoice}
                  onChange={(e) => {
                    const v = e.target.value;
                    setAmountChoice(v);
                    if (v === 'stage') setTotalAmount(String(suggestion.amount));
                    if (v === 'whole') setTotalAmount(String(suggestion.remaining || suggestion.total));
                  }}
                >
                  <FormControlLabel
                    value="stage"
                    control={<Radio />}
                    label={(
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {multiStage ? `Stage ${suggestion.stageIndex} of ${suggestion.stageCount}: ${suggestion.stageName}` : (suggestion.stageName || 'This stage')} — {formatCurrency(suggestion.amount)}
                          {multiStage && <Box component="span" sx={{ color: statusColors.success, ml: 1, fontSize: 12, fontWeight: 700 }}>Recommended</Box>}
                        </Typography>
                        <Typography variant="caption" sx={{ color: text.secondary }}>
                          {multiStage ? 'The rest is asked for stage by stage, as each one comes up.' : 'What this stage needs before work can start.'}
                        </Typography>
                      </Box>
                    )}
                    sx={{ alignItems: 'flex-start', mb: 1 }}
                  />
                  {multiStage && (suggestion.remaining || suggestion.total) > suggestion.amount && (
                    <FormControlLabel
                      value="whole"
                      control={<Radio />}
                      label={(
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Whole project — {formatCurrency(suggestion.remaining || suggestion.total)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: text.secondary }}>
                            Every stage funded now; still released one stage at a time as you approve.
                          </Typography>
                        </Box>
                      )}
                      sx={{ alignItems: 'flex-start', mb: 1 }}
                    />
                  )}
                  <FormControlLabel value="custom" control={<Radio />} label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Another amount</Typography>} />
                </RadioGroup>
              </FormControl>
            )}

            <TextField
              fullWidth
              label="Funding Amount"
              type="number"
              value={totalAmount}
              onChange={(e) => { setTotalAmount(e.target.value); if (staged) setAmountChoice('custom'); }}
              InputProps={{
                startAdornment: <InputAdornment position="start">{project?.currency || 'USD'}</InputAdornment>
              }}
              helperText={
                staged && suggestion.available > 0
                  ? `Already in the wallet: ${formatCurrency(suggestion.available)}. Quotation total: ${formatCurrency(suggestion.total)}.`
                  : (project?.budget ? `Quotation total: ${formatCurrency(project.budget)}` : '')
              }
              sx={{ mb: 3 }}
            />

            <FormControl fullWidth sx={{ mb: 3 }}>
              <FormLabel sx={{ mb: 1, fontWeight: 600 }}>Payment Method</FormLabel>
              <RadioGroup
                value={fundingMethod}
                onChange={(e) => setFundingMethod(e.target.value)}
              >
                <FormControlLabel
                  value="debit_card"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">Debit Card</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Instant processing via Stripe
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="credit_card"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">Credit Card</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Visa, Mastercard, American Express
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="ach"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">ACH Transfer</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Direct bank transfer — 2-5 business days
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="wire"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">Wire Transfer</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Same-day or next business day
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            <Alert severity="info" icon={<Lock fontSize="small" />} sx={{ mb: 2 }}>
              <strong>Your card details are entered on the next screen.</strong> Press the button
              below and Stripe's secure payment page opens — that's where you type your card number,
              expiry and CVC. Nothing is charged until you confirm there.
            </Alert>

            {/* The moment a client most wants a second pair of eyes on the
                ground is right where the money is. One tick, same payment;
                on by default for clients outside Zimbabwe. */}
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: `1px solid ${stageCheck ? brand.primary : border.light}`, bgcolor: stageCheck ? `${brand.primary}0d` : 'transparent' }}>
              <FormControlLabel
                control={<Checkbox checked={stageCheck} onChange={(e) => setStageCheck(e.target.checked)} />}
                label={(
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Add an independent stage check — {formatCurrency(STAGE_CHECK_FEE_USD)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: text.secondary }}>
                      A verified Eqaya person, separate from your contractor, visits the site and sends you dated, GPS-tagged photos and a written report on {suggestion?.stageName ? `"${suggestion.stageName}"` : 'the work'} before you release the money. Charged with this deposit.
                    </Typography>
                  </Box>
                )}
                sx={{ alignItems: 'flex-start', m: 0 }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Payment Summary
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Wallet Funding</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {totalAmount ? formatCurrency(parseFloat(totalAmount)) : '$0.00'}
                </Typography>
              </Box>
              {stageCheck && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Independent stage check</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(STAGE_CHECK_FEE_USD)}</Typography>
                </Box>
              )}
              {stageCheck && totalAmount && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, pt: 1, borderTop: `1px dashed ${border.light}` }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Charged today</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(parseFloat(totalAmount) + STAGE_CHECK_FEE_USD)}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                <Typography variant="body2" color="text.secondary">
                  {fundingMethod === 'debit_card' ? 'Debit Card' :
                   fundingMethod === 'credit_card' ? 'Credit Card' :
                   fundingMethod === 'ach' ? 'ACH Transfer' : 'Wire Transfer'}
                </Typography>
              </Box>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: { minHeight: fullScreen ? undefined : '70vh' }
      }}
    >
      <DialogCloseButton onClick={onClose} />
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalance color="primary" />
          <Typography variant="h6">{skipTerms ? 'Add Money to Wallet' : 'Set Up Wallet'}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {!skipTerms && (
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {activeStep > 0 && !skipTerms && (
          <Button onClick={handleBack} disabled={loading}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={activeStep === 0 && !termsAccepted}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleFundWallet}
            disabled={loading || !totalAmount || parseFloat(totalAmount) <= 0}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
            sx={{
              background: brand.gradient,
              '&:hover': {
                background: brand.gradientHover
              }
            }}
          >
            {loading ? 'Opening secure payment...' : 'Continue to enter card details'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EscrowSetupDialog;
